import { useState } from 'react';
import logoOnDark from '../assets/ipc-global-logo-on-dark.svg';
import { BRAND, SLOGAN, ABOUT_LINE, copyrightLine } from '../lib/brand.js';
import ExternalLink from './ExternalLink.jsx';

/**
 * The compact IPC Global site footer (portal workbench spec): the site's
 * navy band with the on-dark logo, the tagline, the site links, contact,
 * legal links, the copyright line and the trademark notice. The logo is
 * decorative here (alt=""): the visible "onGuard by IPC Global" line and the
 * copyright line name IPC Global (the header carries the onGuard lockup only). Below 768 px the columns stack.
 */
export default function BrandFooter({ year = new Date().getFullYear(), securityLink = true }) {
  const [aboutOpen, setAboutOpen] = useState(false);
  const linkCls = 'text-white underline underline-offset-2 hover:no-underline';
  return (
    <footer className="on-dark bg-ipc-primary text-white">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-4 py-8 md:grid-cols-4">
        <div className="space-y-3 md:col-span-2">
          <div className="flex items-center gap-3">
            <img src={logoOnDark} alt="" className="h-8 w-auto" />
            <span aria-hidden="true" className="h-6 w-px bg-white/30" />
            <p className="text-sm font-semibold">onGuard by IPC Global</p>
          </div>
          <p className="max-w-md text-sm text-white/80">{BRAND.tagline}</p>
          <div className="text-xs text-white/80">
            <p className="flex flex-wrap items-baseline gap-x-3">
              <span>{SLOGAN}</span>
              <button
                type="button"
                aria-expanded={aboutOpen}
                aria-controls="about-onguard"
                onClick={() => setAboutOpen((o) => !o)}
                className="rounded underline underline-offset-2 hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                About
              </button>
            </p>
            {aboutOpen && <p id="about-onguard" className="mt-2 max-w-md">{ABOUT_LINE}</p>}
          </div>
        </div>
        <nav aria-label="IPC Global" className="text-sm">
          <ul className="space-y-2">
            {BRAND.links.map((link) => (
              <li key={link.href}>
                <ExternalLink href={link.href} className={linkCls}>{link.label}</ExternalLink>
              </li>
            ))}
          </ul>
        </nav>
        <address className="space-y-2 text-sm not-italic">
          <p><a className={linkCls} href={`mailto:${BRAND.contact.salesEmail}`}>{BRAND.contact.salesEmail}</a></p>
          <p><a className={linkCls} href={`mailto:${BRAND.contact.supportEmail}`}>{BRAND.contact.supportEmail}</a></p>
          <p>
            <a className={linkCls} href={BRAND.contact.phoneHref}>{BRAND.contact.phoneDisplay}</a>{' '}
            <span className="text-white/80">({BRAND.contact.phoneExtensions})</span>
          </p>
        </address>
      </div>
      <div className="border-t border-white/15">
        <div className="mx-auto max-w-7xl space-y-2 px-4 py-4 text-xs text-white/80">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <p>{copyrightLine(year)}</p>
            <ul className="flex flex-wrap gap-x-4 gap-y-1">
              {securityLink && <li><a href="/security" className={linkCls}>Trust and security</a></li>}
              {BRAND.legal.map((link) => (
                <li key={link.href}>
                  <ExternalLink href={link.href} className={linkCls}>{link.label}</ExternalLink>
                </li>
              ))}
            </ul>
          </div>
          <p>{BRAND.trademarkNotice}</p>
        </div>
      </div>
    </footer>
  );
}
